// packages/web/src/lib/tenant.ts — A2.1b's stopgap tenant-selection.
//
// GAP, flagged rather than papered over: this codebase's server has no
// "list my tenant memberships" read (mapped and confirmed absent —
// packages/server/src/plugins/auth.ts only has POST /auth/tenants to
// CREATE one; nothing reads a caller's existing memberships back). D24's
// model sends X-Tenant-Id on every /api/* call, chosen client-side — but
// with no way to ask the server "which tenants am I in", this PWA has no
// server-backed way to discover the answer after a bare login.
//
// Stopgap for THIS chunk (a single-tenant demo, per the seedActorDemo.ts
// dev script): the tenant id is captured once, client-side only, via a
// `?tenant=<id>` query param on the very first load (the seed script
// prints a ready-to-use URL carrying it) and then persisted to
// localStorage for every later visit. If neither is present, the app asks
// once (screens/ConnectWorkspace.tsx) rather than silently failing.
//
// This is explicitly NOT a multi-tenant switcher — revisit once a real
// "list my tenants" endpoint exists; noted in the A2.1b report rather than
// inventing that endpoint here.
const TENANT_STORAGE_KEY = 'vowric_tenant_id';

export function getStoredTenantId(): string | null {
  try {
    return window.localStorage.getItem(TENANT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setStoredTenantId(tenantId: string): void {
  try {
    window.localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
  } catch {
    // localStorage unavailable (private-browsing edge cases) — the
    // ConnectWorkspace screen simply re-asks next load; not fatal.
  }
}

export function clearStoredTenantId(): void {
  try {
    window.localStorage.removeItem(TENANT_STORAGE_KEY);
  } catch {
    // see setStoredTenantId
  }
}

/** Seeds localStorage from a `?tenant=<id>` query param, once, if present.
 * Safe to call unconditionally on every boot — a no-op once the param is
 * gone from the URL (main.tsx clears it after the first read). */
export function bootstrapTenantFromUrl(): void {
  const fromUrl = new URLSearchParams(window.location.search).get('tenant');
  if (fromUrl) setStoredTenantId(fromUrl);
}
